import React, { useState } from 'react';
import { Card, CardContent } from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import { ShieldCheck, TrendingUp, DollarSign, Percent, Check, Info } from "lucide-react";
import { cn } from "@shared/lib/utils";
import { GenericTable } from "@shared/components/GenericTable";
import { QuickStatsCard } from "@shared/components/QuickStatsCard";
import { useTranslation } from "react-i18next";

function SecurityDepositNoInterest({ rule }: { rule: any }) {
  const { t } = useTranslation();
  const amountVal = Number(rule?.amount);
  const amount = !isNaN(amountVal) ? `$${amountVal.toFixed(2)}` : t("NA");
  const eligibleAfter = rule?.eligibleAfter ?? rule?.eligible_after ?? 0;
  const eligibleUnit = rule?.eligibleAfterUnit || rule?.eligible_after_unit || 'months';
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 flex flex-col justify-center">
        <p className="text-[10px] font-bold text-gray-400 mb-1">{t("Required Amount")}</p>
        <p className="font-bold text-[14px] text-gray-900">{amount}</p>
      </div>
      <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 flex flex-col justify-center">
        <p className="text-[10px] font-bold text-gray-400 mb-1">{t("Basis")}</p>
        <p className="font-bold text-[14px] text-gray-900">{rule?.basis ? rule.basis.toUpperCase() : t("NA")}</p>
      </div>
      <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 flex flex-col justify-center">
        <p className="text-[10px] font-bold text-gray-400 mb-1">{t("Refund Eligibility")}</p>
        <p className="font-bold text-[14px] text-emerald-700">{eligibleAfter > 0 ? t("After {{count}} {{unit}}", { count: eligibleAfter, unit: eligibleUnit }) : t("Immediate")}</p>
      </div>
    </div>
  );
}

function SecurityDepositWithInterest({ rule }: { rule: any }) {
  const { t } = useTranslation();
  const amountVal = Number(rule?.amount);
  const amount = !isNaN(amountVal) ? `$${amountVal.toFixed(2)}` : t("NA");
  const interestTiers = rule?.interestTiers || rule?.interest_tiers || [];
  const primaryRate = interestTiers.length > 0 ? `${interestTiers[0].rate}% p.a.` : t("Variable");
  const eligibleAfter = rule?.eligibleAfter ?? rule?.eligible_after ?? 0;
  const eligibleUnit = rule?.eligibleAfterUnit || rule?.eligible_after_unit || 'months';
  
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <QuickStatsCard
          title={t("Required Amount")}
          displayValue={amount}
          icon={
            <div className="w-10 h-10 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center">
              <DollarSign className="w-5 h-5" />
            </div>
          }
          className="pt-6 shadow-sm"
        />
        <QuickStatsCard
          title={t("Interest Rate")}
          displayValue={primaryRate}
          icon={
            <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <TrendingUp className="w-5 h-5" />
            </div>
          }
          className="pt-6 shadow-sm"
        />
        <QuickStatsCard
          title={t("Basis")}
          displayValue={rule?.basis ? rule.basis.toUpperCase() : t("NA")}
          icon={
            <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <Percent className="w-5 h-5" />
            </div>
          }
          className="pt-6 shadow-sm"
        />
        <QuickStatsCard
          title={t("Refund Eligibility")}
          displayValue={eligibleAfter > 0 ? t("After {{count}} {{unit}}", { count: eligibleAfter, unit: eligibleUnit }) : t("Immediate")}
          icon={
            <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
          }
          className="pt-6 bg-emerald-50/40 border-emerald-100 shadow-sm"
        />
      </div>

      {interestTiers.length > 0 && (
        <div className="bg-gray-50 text-gray-600 border border-gray-100 rounded-xl p-4 text-[12px] flex items-center gap-2 font-medium mb-4">
          <Info className="w-4 h-4 text-gray-400" />
          {t("Interest Tiers:")} {interestTiers.map((tTier: any) => `${tTier.rate}% (${t('planAndDeposit.securityDeposit.monthsTo', { from: tTier.fromMonth || tTier.from_month, to: tTier.toMonth || tTier.to_month })})`).join(', ')}
        </div>
      )}
    </div>
  );
}

export function SecurityDeposit({ planConfig }: { planConfig?: any }) {
  const { t } = useTranslation();
  const [view, setView] = useState<'with-interest' | 'no-interest'>('with-interest');

  const deposits = planConfig?.result?.deposits || planConfig?.deposits;
  const rules = Array.isArray(deposits?.rules) ? deposits.rules : (deposits?.rule ? [deposits.rule] : []);
  const primaryRule = rules[0];
  
  const isInterestBearing = primaryRule?.payInterest === true || primaryRule?.pay_interest === true || String(primaryRule?.payInterest).toLowerCase() === 'true' || String(primaryRule?.pay_interest).toLowerCase() === 'true';
  const isEnabled = deposits?.enabled === true || String(deposits?.enabled).toLowerCase() === 'true';

  // Sync initial view with the plan's configuration
  React.useEffect(() => {
    if (primaryRule) {
      setView(isInterestBearing ? 'with-interest' : 'no-interest');
    }
  }, [isInterestBearing, primaryRule]);

  if (!isEnabled || !rules || rules.length === 0) {
    return null;
  }

  return (
    <Card className="shadow-sm border-gray-200 bg-white rounded-xl overflow-hidden">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-2 pb-6 border-b border-gray-100">
          <div className="w-10 h-10 rounded-xl bg-emerald-50/80 text-emerald-600 flex items-center justify-center border border-emerald-100/50">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <h3 className="text-[15px] font-bold text-gray-900">{t("Security Deposit")}</h3>
              <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50 rounded-full font-bold text-[10px] px-2 py-0 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> {t("Interest-Bearing")}
              </Badge>
            </div>
            <p className="text-[12px] text-gray-500 font-medium">
              {primaryRule?.code || "DEP-CONFIG"} - {primaryRule?.depositName || primaryRule?.deposit_name || t("Security Deposit Rule")}
            </p>
          </div>
          <div className="ml-auto">
            <Badge variant="secondary" className="bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100 font-bold px-3 py-1 rounded-lg text-[11px]">
              {t("Active Rule")}
            </Badge>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-3 mb-6 mt-4">
          <span className="text-[11px] font-bold text-gray-500">{t("View:")}</span>
          <div className="flex items-center bg-gray-50 border border-gray-200 rounded-full p-1">
            <button 
              onClick={() => setView('with-interest')} 
              className={cn("px-4 py-1.5 rounded-full text-[11px] font-bold flex items-center gap-2 transition-colors", view === 'with-interest' ? 'bg-white shadow-sm text-emerald-700 border border-emerald-100' : 'text-gray-500 hover:text-gray-700')}
            >
              <TrendingUp className="w-3.5 h-3.5" /> {t("With Interest")}
              {view === 'with-interest' && <span className="bg-emerald-100 text-emerald-800 text-[9px] px-1.5 rounded">{t("ON")}</span>}
            </button>
            <button 
              onClick={() => setView('no-interest')} 
              className={cn("px-4 py-1.5 rounded-full text-[11px] font-bold flex items-center gap-2 transition-colors", view === 'no-interest' ? 'bg-white shadow-sm text-gray-900 border border-gray-200' : 'text-gray-500 hover:text-gray-700')}
            >
              <ShieldCheck className="w-3.5 h-3.5" /> {t("No Interest")}
            </button>
          </div>
        </div>

        {/* Dynamic Content */}
        {view === 'with-interest' ? <SecurityDepositWithInterest rule={primaryRule} /> : <SecurityDepositNoInterest rule={primaryRule} />}

      </CardContent>
    </Card>
  );
}
